import { createPortal } from 'react-dom'
import QRCode from 'react-qr-code'
import type { Aggregator } from '../domain/types'
import { env } from '../config/env'

function formatPrintDate(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) {return value}
  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'America/Sao_Paulo',
  }).format(d)
}

/**
 * Bilhete da jornada inteira — mesmo layout do bilhete de ticket individual
 * (`TicketDetailModal`), sem os campos específicos de UM ticket (nome do
 * serviço, etapas, sala/horário do recurso, previsão) e sem a `accessKey`
 * (que é por-ticket, não da jornada). Mostra: local, código da jornada
 * (`friendlyCode` do primeiro ticket, resolvido pelo adapter — nunca
 * derivado no front), data e QR + hint.
 */
export function JourneyTicketPrint({ agg, printing }: { agg: Aggregator; printing: boolean }) {
  if (!printing) {return null}
  if (agg.tickets.length === 0) {return null}
  if (typeof document === 'undefined') {return null} // SSR safety

  const codigo = agg.friendlyCode ?? ''
  const baseUrl = env.trackerUrl
  const safeBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/'
  const qrUrl = `${safeBaseUrl}${agg.tickets[0].accessKey ?? ''}`
  const localizacao = agg.localizacao.nome
  const printDate = formatPrintDate(agg.criadoEm)

  // React Portal: monta o .print-only direto no document.body, fora da
  // árvore do React. Isso é CRÍTICO para o print funcionar: o .fz-main
  // (ancestor no React tree) recebe `display: none` durante o print via
  // CSS, e qualquer descendente — mesmo com `position: fixed` — fica
  // sem layout porque o parent está fora do flow. Portal garante que o
  // .print-only é sibling do #root no body, então a regra
  // `body.fz-journey-printing #root > :not(.fz-overlay) { display: none }`
  // NÃO o esconde, e o bilhete renderiza normalmente.
  return createPortal(
    <div className="print-only" aria-hidden="true">
      <div className="ticket-details">
        <div className="ticket-details-action">
          <div className="ticket-details-heading">
            <h1 title={localizacao.length > 30 ? localizacao : undefined}>{localizacao}</h1>
          </div>
          <div className="ticket-details-body">
            <div className="session">
              <div className="code">{codigo}</div>
              <div className="date">{printDate}</div>
            </div>
            <div className="qr-code-container">
              <QRCode value={qrUrl} size={78} level="L" bgColor="#ffffff" fgColor="#000000" />
            </div>
            <div className="hint">Apresente este QR no painel · Aguarde ser chamado por {codigo}</div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
